import { GenericTable } from "@shared/components/GenericTable";
import { cn } from "@shared/lib/utils";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableColumn } from "@shared/types/table";
import dayjs from "dayjs";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  useConsumerDetails,
  useConsumerLastConsumption,
  useConsumerLatestBill,
  useMeterdata,
  useMeterReadingStatus,
} from "@features/bx/singleBillGenaration/hooks";
import ConsumptionTable from "./ConsumptionTable";
import { Info } from "lucide-react";
import { Badge } from "@shared/ui/badge";

/* -------------------- Memoized Cells -------------------- */

const CurrentReadingCell = React.memo(
  ({
    value,
    onChange,
  }: {
    value: number | string;
    onChange: (v: number) => void;
  }) => (
    <input
      type="number"
      className="border rounded px-2 py-0.5 w-20 text-sm"
      value={value ?? ""}
      onChange={(e) => onChange(Number(e.target.value))}
      placeholder="Enter reading"
    />
  )
);

const StatusCell = React.memo(
  ({
    value,
    options,
    onChange,
  }: {
    value: string;
    options: any[];
    onChange: (v: string) => void;
  }) => (
    <select
      className="border rounded px-1 py-0 text-xs w-24"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">Select Status</option>
      {options?.map((opt) => (
        <option key={opt.key} value={opt.key}>
          {opt.value}
        </option>
      ))}
    </select>
  )
);

const DateCell = React.memo(
  ({ value, onChange }: { value: string; onChange: (v: string) => void }) => (
    <input
      type="date"
      className="border rounded px-2 py-1 text-xs w-32"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  )
);

/* -------------------- Types -------------------- */

interface MeterReadingsProps {
  consumerDataId?: any;
  invoiceStartDate?: string;
  invoiceEndDate?: string;
  consumerNo?: any;
  meterReadingsUpdate: any;
  onMeterReadingsUpdate?: (readingData: any[]) => void;
}

interface MeterData {
  id: string;
  meterNumber: string;
  deviceNo: string;
  productCode: string;
  previousReading: number;
  previousReadingDate: string;
  previousReadingMeterStatusDisplay: string;
  currentReading: number;
  currentReadingDate: string;
  currentReadingMeterStatus: string;
}

/* -------------------- Component -------------------- */

const MeterReadings: React.FC<MeterReadingsProps> = ({
  consumerDataId,
  invoiceEndDate,
  invoiceStartDate,
  consumerNo,
  meterReadingsUpdate,
  onMeterReadingsUpdate,
}) => {
  const { id } = useParams();
  const CURRENCY = useCurrency();

  // Local state to track meter updates
  const [meterUpdates, setMeterUpdates] = useState<Record<string, any>>({});

  // Refs to track initialization
  const isInitializedRef = useRef(false);
  const meterDataRef = useRef<MeterData[] | undefined>();

  /* -------------------- API Hooks -------------------- */

  const { data: consumerData } = useConsumerDetails(consumerDataId);
  const { data: latestBillData } = useConsumerLatestBill(consumerDataId);
  const { data: meterReadingStatus } = useMeterReadingStatus();

  const meterIds = consumerData?.meters?.map((m: any) => m) || [];

  const { data: meterData } = useMeterdata(
    meterIds,
    invoiceStartDate,
    invoiceEndDate
  );

  const { data:lastConsumptionData} = useConsumerLastConsumption(consumerNo)

  // Update ref when meterData changes
  useEffect(() => {
    if (meterData) {
      meterDataRef.current = meterData;
    }
  }, [meterData]);

  /* -------------------- Initialize from Parent State or API -------------------- */

  useEffect(() => {
    // Don't initialize if already done
    if (isInitializedRef.current) return;

    // Wait for both meter data and parent updates to be available
    if (!meterData) return;

    // Check if we have parent state to restore
    if (
      meterReadingsUpdate &&
      Array.isArray(meterReadingsUpdate) &&
      meterReadingsUpdate.length > 0
    ) {
      // Restore from parent state
      const prefilledUpdates = meterReadingsUpdate.reduce(
        (acc: Record<string, any>, item: any) => {
          acc[item.meter_id] = {
            current_reading: item.current_reading,
            currentReadingDate: item.reading_date,
            current_reading_meter_status: item.status,
          };
          return acc;
        },
        {}
      );

      setMeterUpdates(prefilledUpdates);
      isInitializedRef.current = true;
    } else {
      // Initialize from API data (patch the current values from API)
      const apiBasedUpdates = meterData.reduce(
        (acc: Record<string, any>, meter: MeterData) => {
          // Only add to updates if API has values
          if (
            meter.currentReading !== null &&
            meter.currentReading !== undefined &&
            meter.currentReadingDate &&
            meter.currentReadingMeterStatus
          ) {
            acc[meter.id] = {
              current_reading: meter.currentReading,
              currentReadingDate: meter.currentReadingDate,
              current_reading_meter_status: meter.currentReadingMeterStatus,
            };
          }
          return acc;
        },
        {}
      );

      setMeterUpdates(apiBasedUpdates);
      isInitializedRef.current = true;
    }
  }, [meterData, meterReadingsUpdate]);

  /* -------------------- Helpers -------------------- */

  const getMeterValue = useCallback(
    (meterId: string, field: string, originalValue: any) =>
      meterUpdates[meterId]?.[field] ?? originalValue,
    [meterUpdates]
  );

  const parseFlexibleDateToString = useCallback((input: any) => {
    if (!input) return "";
    const date = dayjs(input);
    return date.isValid() ? date.format("YYYY-MM-DD") : "";
  }, []);

  const calculateConsumption = useCallback(
    (current: string, previous: string) => {
      const diff = Number(current) - Number(previous);
      return diff >= 0 ? diff : 0;
    },
    []
  );

  // Build and send reading data to parent
  const buildAndSendReadingData = useCallback(
    (updatedMeterUpdates: Record<string, any>) => {
      if (!meterDataRef.current || !onMeterReadingsUpdate) return;

      const currentMeterData = meterDataRef.current;

      const readingData = currentMeterData
        .filter((meter) => {
          const meterUpd = updatedMeterUpdates[meter.id] || {};
          const currentReading =
            meterUpd.current_reading ?? meter.currentReading;
          const currentStatus =
            meterUpd.current_reading_meter_status ??
            meter.currentReadingMeterStatus;
          const currentDate =
            meterUpd.currentReadingDate ?? meter.currentReadingDate;

          return (
            currentReading !== null &&
            currentReading !== undefined &&
            currentReading !== "" &&
            currentStatus !== null &&
            currentStatus !== undefined &&
            currentStatus !== "" &&
            currentDate !== null &&
            currentDate !== undefined &&
            currentDate !== ""
          );
        })
        .map((meter) => {
          const meterUpd = updatedMeterUpdates[meter.id] || {};
          const currentReading =
            meterUpd.current_reading ?? meter.currentReading;
          const currentStatus =
            meterUpd.current_reading_meter_status ??
            meter.currentReadingMeterStatus;
          const currentDate =
            meterUpd.currentReadingDate ?? meter.currentReadingDate;

          return {
            meter_id: meter.id,
            meter_number: meter.meterNumber,
            current_reading: Number(currentReading),
            reading_date: parseFlexibleDateToString(currentDate),
            reading_source: 1,
            status: currentStatus,
            product_code: meter.productCode,
          };
        });

      onMeterReadingsUpdate(readingData);
    },
    [onMeterReadingsUpdate, parseFlexibleDateToString]
  );

  const handleMeterChange = useCallback(
    (meterId: string, field: string, value: any) => {
      setMeterUpdates((prev) => {
        const updated = {
          ...prev,
          [meterId]: {
            ...prev[meterId],
            [field]: value,
          },
        };

        // Send updated data to parent
        buildAndSendReadingData(updated);

        return updated;
      });
    },
    [buildAndSendReadingData]
  );

  /* -------------------- Columns (MEMOIZED) -------------------- */

  const meterColumns = useMemo<TableColumn<MeterData>[]>(
    () => [
      {
        key: "meterInfo",
        header: "Resource ID/Serial No",
        type: "custom",
        render: (_, row) => `${row.meterNumber}/${row.deviceNo}`,
      },
      {
        key: "productCode",
        header: "Utility Service",
        type: "custom",
        render: (value) => value ? <Badge>{value}</Badge> : "NA",
      },
      {
        key: "previousReading",
        header: "Last Reading",
        type: "text",
      },
      {
        key: "previousReadingMeterStatusDisplay",
        header: "Prev Status",
        type: "text",
      },
      {
        key: "previousReadingDate",
        header: "Last Date",
        type: "text",
      },
      {
        key: "currentReading",
        header: "Current Reading",
        type: "custom",
        render: (_, row) => (
          <CurrentReadingCell
            value={getMeterValue(row.id, "current_reading", row.currentReading)}
            onChange={(v) => handleMeterChange(row.id, "current_reading", v)}
          />
        ),
      },
      {
        key: "currentReadingMeterStatus",
        header: "Curr Status",
        type: "custom",
        render: (_, row) => (
          <StatusCell
            value={getMeterValue(
              row.id,
              "current_reading_meter_status",
              row.currentReadingMeterStatus
            )}
            options={meterReadingStatus}
            onChange={(v) =>
              handleMeterChange(row.id, "current_reading_meter_status", v)
            }
          />
        ),
      },
      {
        key: "currentReadingDate",
        header: "Current Date",
        type: "custom",
        render: (_, row) => (
          <DateCell
            value={getMeterValue(
              row.id,
              "currentReadingDate",
              row.currentReadingDate
            )}
            onChange={(v) => handleMeterChange(row.id, "currentReadingDate", v)}
          />
        ),
      },
      {
        key: "consumption",
        header: "Consumption",
        type: "custom",
        render: (_, row) => {
          const current = getMeterValue(
            row.id,
            "current_reading",
            row.currentReading
          );
          const consumption = calculateConsumption(
            current?.toString() || "0",
            row.previousReading?.toString() || "0"
          );
          return (
            <span
              className={cn(
                "font-medium",
                consumption < 0 ? "text-red-600" : "text-green-600"
              )}
            >
              {consumption} units
            </span>
          );
        },
      },
    ],
    [
      getMeterValue,
      handleMeterChange,
      meterReadingStatus,
      parseFlexibleDateToString,
      calculateConsumption,
    ]
  );

  /* -------------------- Render -------------------- */

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border shadow-sm">
        <div className="px-6 py-2">
          <h3 className="card-heading-text">Readings & Consumption</h3>
        </div>

        <div className="p-4 pt-2">
          <GenericTable
            data={meterData || []}
            columns={meterColumns}
            rowKey="id"
            loading={false}
            emptyMessage="No data available"
          />
        </div>

        <div className="px-4">
          <p className="card-heading-text pl-2">Last 5 Bills Consumptions</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 mb-6">
            <Info className="h-5 w-5 text-blue-600 mt-0.5" />
            <p className="text-sm text-blue-900">
              Use the below filter to check utility wise filtered last 5 bills
              consumptions.
            </p>
          </div>
          <ConsumptionTable data={lastConsumptionData} />
        </div>
      </div>
    </div>
  );
};

export default MeterReadings;
